import React, { useState, useEffect } from 'react';
import { ExternalLink, Download, X } from 'lucide-react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Button } from '@/components/ui/Button/Button';
import { LoadingState } from '@/components/ui/LoadingState';

export interface FileViewerFile {
  url: string;
  name: string;
  mimeType?: string;
  size?: number;
}

interface FileViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  file: FileViewerFile | null;
}

export function FileViewerModal({ isOpen, onClose, file }: FileViewerModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);

  useEffect(() => {
    if (file && isOpen) {
      setLoading(true);
      setError(null);
      
      // If it's a blob URL or data URL, use it directly
      if (file.url.startsWith('blob:') || file.url.startsWith('data:')) {
        setFileUrl(file.url);
        setLoading(false);
        return;
      }

      // For external URLs, try to fetch and create blob URL
      // This handles CORS and authentication issues
      fetch(file.url)
        .then(response => {
          if (!response.ok) {
            throw new Error(`Failed to load file: ${response.status} ${response.statusText}`);
          }
          return response.blob();
        })
        .then(blob => {
          const url = URL.createObjectURL(blob);
          setFileUrl(url);
          setLoading(false);
        })
        .catch(err => {
          console.error('Failed to load file:', err);
          setError(err.message || 'Failed to load file');
          setLoading(false);
          // Fallback: try using the URL directly (might work for some files)
          setFileUrl(file.url);
        });
    } else {
      // Cleanup blob URL when closing
      if (fileUrl && fileUrl.startsWith('blob:')) {
        URL.revokeObjectURL(fileUrl);
      }
      setFileUrl(null);
      setError(null);
    }

    // Cleanup on unmount
    return () => {
      if (fileUrl && fileUrl.startsWith('blob:')) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [file, isOpen]);

  if (!file) return null;

  const mimeType = (file.mimeType || '').toLowerCase();
  const fileName = file.name || 'File';
  const isImage = mimeType.startsWith('image/');
  const isPdf = mimeType === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');
  const isVideo = mimeType.startsWith('video/');
  const isAudio = mimeType.startsWith('audio/');
  const isText = mimeType.startsWith('text/') || fileName.match(/\.(txt|csv|json|md|log)(\?.*)?$/i);

  const handleDownload = () => {
    if (!fileUrl) return;
    
    const a = document.createElement('a');
    a.href = fileUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleOpenInNewTab = () => {
    if (!fileUrl) return;
    window.open(fileUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={fileName}
      size="xl"
      className="max-w-6xl"
      footer={
        <div className="flex items-center justify-between w-full">
          <div className="text-xs text-muted-foreground">
            {file.size && (
              <span>{(file.size / 1024).toFixed(1)} KB</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleOpenInNewTab}
              leftIcon={<ExternalLink className="w-4 h-4" />}
            >
              Open in New Tab
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleDownload}
              leftIcon={<Download className="w-4 h-4" />}
            >
              Download
            </Button>
          </div>
        </div>
      }
    >
      <div className="w-full h-[70vh] bg-gray-50 rounded border overflow-hidden">
        {loading && (
          <div className="flex items-center justify-center h-full">
            <LoadingState text="Loading file..." />
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center h-full text-center p-6">
            <X className="w-12 h-12 text-red-400 mb-3" />
            <p className="text-sm text-gray-600 mb-2">Failed to load file</p>
            <p className="text-xs text-gray-500">{error}</p>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleOpenInNewTab}
              className="mt-4"
            >
              Try Opening in New Tab
            </Button>
          </div>
        )}

        {!loading && !error && fileUrl && (
          <>
            {isImage && (
              <img
                src={fileUrl}
                alt={fileName}
                className="w-full h-full object-contain"
                onError={() => setError('Failed to load image')}
              />
            )}

            {isPdf && (
              <iframe
                title={fileName}
                src={`${fileUrl}#toolbar=1&statusbar=0&navpanes=0`}
                className="w-full h-full border-0"
                onError={() => setError('Failed to load PDF')}
              />
            )}

            {isVideo && (
              <video
                src={fileUrl}
                controls
                className="w-full h-full object-contain"
                onError={() => setError('Failed to load video')}
              />
            )}

            {isAudio && (
              <div className="flex items-center justify-center h-full p-8">
                <audio
                  src={fileUrl}
                  controls
                  className="w-full max-w-xl"
                  onError={() => setError('Failed to load audio')}
                />
              </div>
            )}

            {isText && (
              <iframe
                title={fileName}
                src={fileUrl}
                className="w-full h-full bg-white border-0"
                onError={() => setError('Failed to load text file')}
              />
            )}

            {!isImage && !isPdf && !isVideo && !isAudio && !isText && (
              <div className="flex flex-col items-center justify-center h-full text-center p-6">
                <svg
                  className="w-12 h-12 text-gray-400 mb-3"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.586-6.586a2 2 0 000-2.828z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 13l3-3"
                  />
                </svg>
                <p className="text-sm text-gray-600 mb-2">Preview not available for this file type</p>
                <p className="text-xs text-gray-500 mb-4">
                  {mimeType || 'Unknown file type'}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleOpenInNewTab}
                    leftIcon={<ExternalLink className="w-4 h-4" />}
                  >
                    Open in New Tab
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleDownload}
                    leftIcon={<Download className="w-4 h-4" />}
                  >
                    Download
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

